import { Badge } from '@awpmarine/ui/components/ui/badge';
import { Link } from '@tanstack/react-router';
import { cn } from 'cn';
import { ArrowRightIcon } from 'lucide-react';

import { contentLang } from '@/components/content-language';
import { formatDate } from '@/lib/dates';
import { isTranslated, news } from '@/lib/source';
import { m } from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';

export interface NewsItem {
  slug: string;
  title: string;
  description: string | null;
  date: string;
  source: string | null;
  tags: string[];
  /** Written in the page's locale, rather than standing in from English. */
  translated: boolean;
}

/** Every article, newest first, flattened to what the lists need. */
export function newsItems(): NewsItem[] {
  return news
    .getPages(getLocale())
    .sort((a, b) => Date.parse(b.data.date) - Date.parse(a.data.date))
    .map((post) => ({
      slug: post.slugs.join('/'),
      title: post.data.title,
      description: post.data.description ?? null,
      date: post.data.date,
      source: post.data.source?.name ?? null,
      tags: post.data.tags ?? [],
      translated: isTranslated(post),
    }));
}

/**
 * A text-only card. The covers that come with syndicated articles are mostly too small to show at
 * any size, so news is laid out without them.
 */
export function NewsCard({ item }: { item: NewsItem }) {
  return (
    <Link
      to='/news/$'
      params={{ _splat: item.slug }}
      className='border-line flex min-h-55 flex-col gap-3.5 rounded-[20px] border bg-white p-6.5 transition-colors hover:border-brand'
    >
      <span className='text-subtle flex flex-wrap items-center gap-2.5 text-[13px]'>
        {item.tags[0] && (
          <Badge className='h-auto rounded-full bg-[#eef3fa] px-2.5 py-1 text-[13px] font-semibold text-[#1f4a80]'>
            {item.tags[0]}
          </Badge>
        )}
        <time dateTime={item.date}>{formatDate(item.date)}</time>
        {!item.translated && <EnglishBadge />}
      </span>
      <span lang={contentLang(item.translated)} className='text-xl/snug font-bold'>
        {item.title}
      </span>
      <span className='text-subtle mt-auto flex items-center justify-between gap-3 text-sm'>
        {item.source ? m.news_source({ source: item.source }) : <span />}
        <ArrowRightIcon className='size-4.5 shrink-0 text-brand' />
      </span>
    </Link>
  );
}

/** A row in the news list: date, title and source, tag, and an arrow. */
export function NewsRow({ item, className }: { item: NewsItem; className?: string }) {
  return (
    <Link
      to='/news/$'
      params={{ _splat: item.slug }}
      className={cn('group border-line flex flex-wrap items-center gap-x-8 gap-y-2 border-t py-6', className)}
    >
      <time dateTime={item.date} className='text-subtle w-full sm:w-37.5'>
        {formatDate(item.date)}
      </time>
      <span className='min-w-0 flex-[1_1_420px]'>
        <span
          lang={contentLang(item.translated)}
          className='mb-1.5 block text-xl/snug font-bold transition-colors group-hover:text-brand'
        >
          {item.title}
        </span>
        {!item.translated && <EnglishBadge />}
        {item.source && <span className='text-subtle text-sm'>{m.news_source({ source: item.source })}</span>}
      </span>
      {item.tags[0] && (
        <Badge className='h-auto rounded-full bg-[#eef3fa] px-2.5 py-1 text-[13px] font-semibold text-[#1f4a80]'>
          {item.tags[0]}
        </Badge>
      )}
      <span className='bg-mist hidden size-10 shrink-0 items-center justify-center rounded-full text-brand sm:flex'>
        <ArrowRightIcon className='size-4.5' />
      </span>
    </Link>
  );
}

/** Marks an article listed in another locale before it has been translated. */
function EnglishBadge() {
  return (
    <Badge variant='outline' className='h-auto rounded-full px-2.5 py-1 text-[13px] font-semibold'>
      {m.content_english_badge()}
    </Badge>
  );
}
